import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Loader2, Play, ShoppingCart, ChevronRight, Eye, Check } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Helper to detect YouTube URLs
const isYouTubeUrl = (url) => {
  return url && (url.includes('youtube.com') || url.includes('youtu.be'));
};

// Helper to convert YouTube URLs to embed format
const getYouTubeEmbedUrl = (url, autoplay = true) => {
  if (!url) return null;
  
  // Match various YouTube URL formats
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/
  ];
  
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) {
      if (autoplay) {
        return `https://www.youtube.com/embed/${match[1]}?autoplay=1&mute=1&loop=1&playlist=${match[1]}&controls=0&showinfo=0`;
      }
      return `https://www.youtube.com/embed/${match[1]}`;
    }
  }
  return null;
};

const BrandPage = () => {
  const { slug } = useParams();
  const { addToCart } = useCart();
  const { user } = useAuth();
  const [brand, setBrand] = useState(null);
  const [sections, setSections] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addedItems, setAddedItems] = useState({}); // Track which items show "Added" state
  const videoRef = useRef(null);

  useEffect(() => {
    fetchBrandData();
  }, [slug]);

  const fetchBrandData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [brandRes, sectionsRes, productsRes] = await Promise.all([
        axios.get(`${API_URL}/brands/${slug}`),
        axios.get(`${API_URL}/brands/${slug}/sections`),
        axios.get(`${API_URL}/brands/${slug}/products`)
      ]);
      
      setBrand(brandRes.data);
      
      // Check if there's a products section in sections, if not add it at position 0
      let brandSections = sectionsRes.data || [];
      const hasProductsSection = brandSections.some(s => s.section_type === 'products');
      
      if (!hasProductsSection && productsRes.data.length > 0) {
        // Insert products section at the beginning (right after hero)
        brandSections = [
          { id: 'products-section', section_type: 'products', title: `${brandRes.data.name} Products`, order: -1 },
          ...brandSections
        ];
      }
      
      // Sort sections by order
      brandSections.sort((a, b) => (a.order || 0) - (b.order || 0));
      
      setSections(brandSections);
      setProducts(productsRes.data);
    } catch (err) {
      console.error('Error fetching brand:', err);
      setError('Brand not found');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (product) => {
    // Prevent double-clicks - if item is in "Added" state, ignore
    if (addedItems[product.id]) {
      toast.info('Item already in cart! Check your cart to update quantity.');
      return;
    }
    
    if (product.price_on_request) {
      toast.info('Please contact us for pricing on this item');
      return;
    }
    
    addToCart({
      id: product.id,
      name: product.name,
      price: product.price,
      image_url: product.images?.[0] || '/placeholder.jpg',
      quantity: 1,
      type: 'brand_product'
    });
    
    // Show "Added" state for 3 seconds
    setAddedItems(prev => ({ ...prev, [product.id]: true }));
    toast.success(`${product.name} added to cart!`);
    
    // Reset after 3 seconds
    setTimeout(() => {
      setAddedItems(prev => ({ ...prev, [product.id]: false }));
    }, 3000);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F0EFEA]">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF6B35]" />
      </div>
    );
  }

  if (error || !brand) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#F0EFEA]">
        <h1 className="text-2xl font-heading font-bold text-[#0A2342] mb-4">Brand Not Found</h1>
        <Link to="/">
          <Button className="bg-[#FF6B35] text-white">Return Home</Button>
        </Link>
      </div>
    );
  }

  const youtubeEmbedUrl = brand?.hero_video ? getYouTubeEmbedUrl(brand.hero_video, true) : null;

  return (
    <div className="min-h-screen bg-[#F0EFEA]" data-testid="brand-page">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-[#0A2342]">
        {brand.hero_type === 'video' && brand.hero_video ? (
          <>
            {isYouTubeUrl(brand.hero_video) ? (
              // YouTube embed - scale up for cover effect
              <div className="relative w-full h-[60vh] sm:h-[65vh] md:h-[70vh] overflow-hidden">
                <iframe
                  className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[177.77vh] min-w-full h-[56.25vw] min-h-full"
                  src={youtubeEmbedUrl}
                  title="Brand Video"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  style={{ pointerEvents: 'none' }}
                />
                <div className="absolute inset-0 bg-black/40" />
                {/* Overlay content for video */}
                <div className="absolute inset-0 flex items-center">
                  <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 lg:px-24 w-full">
                    <div className="max-w-xl sm:max-w-2xl">
                      {brand.hero_title && (
                        <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-heading font-bold text-white mb-2 sm:mb-3 md:mb-4 drop-shadow-lg">
                          {brand.hero_title}
                        </h1>
                      )}
                      {brand.hero_subtitle && (
                        <p className="text-sm sm:text-base md:text-lg lg:text-xl text-white/90 mb-4 sm:mb-6 md:mb-8 drop-shadow">
                          {brand.hero_subtitle}
                        </p>
                      )}
                      {brand.hero_cta_text && brand.hero_cta_link && (
                        <Link to={brand.hero_cta_link}>
                          <Button 
                            size="lg" 
                            className="bg-[#FF6B35] text-white hover:bg-[#FF6B35]/90 shadow-lg text-xs sm:text-sm md:text-base h-10 sm:h-12"
                            data-testid="hero-cta-btn"
                          >
                            {brand.hero_cta_text}
                            <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2" />
                          </Button>
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              // Direct video file
              <div className="relative w-full h-[60vh] sm:h-[65vh] md:h-[70vh]">
                <video
                  ref={videoRef}
                  className="absolute inset-0 w-full h-full object-cover"
                  src={brand.hero_video}
                  autoPlay
                  muted
                  loop
                  playsInline
                  poster={brand.hero_image || ''}
                />
                <div className="absolute inset-0 bg-black/40" />
                {/* Overlay content for video */}
                <div className="absolute inset-0 flex items-center">
                  <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 lg:px-24 w-full">
                    <div className="max-w-xl sm:max-w-2xl">
                      {brand.hero_title && (
                        <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-heading font-bold text-white mb-2 sm:mb-3 md:mb-4 drop-shadow-lg">
                          {brand.hero_title}
                        </h1>
                      )}
                      {brand.hero_subtitle && (
                        <p className="text-sm sm:text-base md:text-lg lg:text-xl text-white/90 mb-4 sm:mb-6 md:mb-8 drop-shadow">
                          {brand.hero_subtitle}
                        </p>
                      )}
                      {brand.hero_cta_text && brand.hero_cta_link && (
                        <Link to={brand.hero_cta_link}>
                          <Button 
                            size="lg" 
                            className="bg-[#FF6B35] text-white hover:bg-[#FF6B35]/90 shadow-lg text-xs sm:text-sm md:text-base h-10 sm:h-12"
                            data-testid="hero-cta-btn"
                          >
                            {brand.hero_cta_text}
                            <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2" />
                          </Button>
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            {/* Hero image - Mobile shows full image, Desktop uses cover */}
            {/* Mobile Layout - Stacked: Image then Text */}
            <div className="sm:hidden">
              <div className="relative w-full">
                <img 
                  src={brand.hero_image || 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=1920'}
                  alt={brand.name || 'Brand hero'}
                  className="w-full h-auto"
                />
              </div>
              <div className="bg-[#0A2342] px-4 py-6">
                {brand.hero_title && (
                  <h1 className="text-2xl font-heading font-bold text-white mb-2 drop-shadow-lg">
                    {brand.hero_title}
                  </h1>
                )}
                {brand.hero_subtitle && (
                  <p className="text-sm text-white/90 mb-4 drop-shadow">
                    {brand.hero_subtitle}
                  </p>
                )}
                {brand.hero_cta_text && brand.hero_cta_link && (
                  <Link to={brand.hero_cta_link}>
                    <Button 
                      size="lg" 
                      className="bg-[#FF6B35] text-white hover:bg-[#FF6B35]/90 shadow-lg text-xs h-10"
                      data-testid="hero-cta-btn"
                    >
                      {brand.hero_cta_text}
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                )}
              </div>
            </div>
            
            {/* Desktop Layout - Overlay text on image */}
            <div className="hidden sm:block relative w-full min-h-[60vh] md:min-h-[70vh]">
              <img 
                src={brand.hero_image || 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=1920'}
                alt={brand.name || 'Brand hero'}
                className="absolute inset-0 w-full h-full object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#0A2342]/80 via-[#0A2342]/50 to-transparent" />
              
              {/* Overlay content */}
              <div className="absolute inset-0 flex items-center">
                <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 w-full">
                  <div className="max-w-2xl">
                    {brand.hero_title && (
                      <h1 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-heading font-bold text-white mb-3 md:mb-4 drop-shadow-lg">
                        {brand.hero_title}
                      </h1>
                    )}
                    {brand.hero_subtitle && (
                      <p className="text-base md:text-lg lg:text-xl text-white/90 mb-6 md:mb-8 drop-shadow">
                        {brand.hero_subtitle}
                      </p>
                    )}
                    {brand.hero_cta_text && brand.hero_cta_link && (
                      <Link to={brand.hero_cta_link}>
                        <Button 
                          size="lg" 
                          className="bg-[#FF6B35] text-white hover:bg-[#FF6B35]/90 shadow-lg text-sm md:text-base h-12"
                          data-testid="hero-cta-btn"
                        >
                          {brand.hero_cta_text}
                          <ChevronRight className="w-5 h-5 ml-2" />
                        </Button>
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </section>

      {/* Brand Description */}
      {brand.description && (
        <section className="py-12 bg-white">
          <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
            <p className="text-lg text-gray-700 leading-relaxed">
              {brand.description}
            </p>
          </div>
        </section>
      )}

      {/* Dynamic Sections - Rendered in order, including Products section */}
      {sections.map((section, index) => (
        <section 
          key={section.id} 
          className={`py-12 ${index % 2 === 0 ? 'bg-[#F0EFEA]' : 'bg-white'}`}
        >
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            {section.title && section.section_type !== 'products' && (
              <h2 className="text-3xl font-heading font-bold text-[#0A2342] mb-6 text-center">
                {section.title}
              </h2>
            )}
            
            {/* Text Section */}
            {section.section_type === 'text' && section.content && (
              <div className="prose prose-lg max-w-4xl mx-auto text-gray-700">
                <p className="whitespace-pre-line">{section.content}</p>
              </div>
            )}
            
            {/* Image Section */}
            {section.section_type === 'image' && section.media_url && (
              <div className="flex justify-center">
                <img 
                  src={section.media_url} 
                  alt={section.title || 'Brand image'} 
                  className="max-w-full h-auto rounded-lg shadow-lg"
                />
              </div>
            )}
            
            {/* Video Section - supports multiple videos via media_urls */}
            {section.section_type === 'video' && (section.media_url || section.media_urls?.length > 0) && (
              <div className={`${section.media_urls?.length > 1 ? 'grid grid-cols-1 md:grid-cols-2 gap-6' : 'flex justify-center'}`}>
                {/* Single video from media_url */}
                {section.media_url && !section.media_urls?.length && (
                  isYouTubeUrl(section.media_url) ? (
                    <div className="w-full max-w-4xl aspect-video mx-auto">
                      <iframe
                        className="w-full h-full rounded-lg shadow-lg"
                        src={getYouTubeEmbedUrl(section.media_url, false)}
                        title="Video"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  ) : (
                    <video 
                      src={section.media_url}
                      controls
                      className="max-w-full rounded-lg shadow-lg mx-auto"
                      style={{ maxHeight: '500px' }}
                    />
                  )
                )}
                
                {/* Multiple videos from media_urls */}
                {section.media_urls?.map((url, idx) => (
                  <div key={idx} className="w-full aspect-video">
                    {isYouTubeUrl(url) ? (
                      <iframe
                        className="w-full h-full rounded-lg shadow-lg"
                        src={getYouTubeEmbedUrl(url, false)}
                        title={`Video ${idx + 1}`}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    ) : (
                      <video 
                        src={url}
                        controls
                        className="w-full h-full rounded-lg shadow-lg object-cover"
                      />
                    )}
                  </div>
                ))}
              </div>
            )}
            
            {/* Gallery Section */}
            {section.section_type === 'gallery' && section.media_urls?.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {section.media_urls.map((url, idx) => (
                  <img 
                    key={idx}
                    src={url} 
                    alt={`Gallery ${idx + 1}`}
                    className="w-full h-48 object-cover rounded-lg shadow hover:shadow-lg transition-shadow"
                  />
                ))}
              </div>
            )}
            
            {/* Products Section - Inline product grid */}
            {section.section_type === 'products' && products.length > 0 && (
              <>
                <h2 className="text-3xl font-heading font-bold text-[#0A2342] mb-8 text-center">
                  {section.title || `${brand.name} Products`}
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {products.map((product) => (
                    <div 
                      key={product.id}
                      className="bg-[#F0EFEA] rounded-lg overflow-hidden hover:shadow-xl transition-all duration-300 group"
                      data-testid={`brand-product-${product.id}`}
                    >
                      <Link to={`/brand-product/${product.id}`}>
                        <div className="relative h-56 overflow-hidden flex items-center justify-center bg-white">
                          <img 
                            src={product.images?.[0] || 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=400'}
                            alt={product.name}
                            className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                          />
                          {product.featured && (
                            <Badge className="absolute top-3 left-3 bg-[#FF6B35] text-white">
                              Featured
                            </Badge>
                          )}
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                            <Eye className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                        </div>
                      </Link>
                      
                      <div className="p-4">
                        <Link to={`/brand-product/${product.id}`}>
                          <h3 className="text-lg font-semibold text-[#0A2342] mb-2 hover:text-[#FF6B35] transition-colors">
                            {product.name}
                          </h3>
                        </Link>
                        
                        <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                          {product.description}
                        </p>
                        
                        <div className="flex items-center justify-between">
                          <div>
                            {product.price_on_request ? (
                              <span className="text-[#FF6B35] font-semibold">Price on Request</span>
                            ) : (
                              <span className="text-xl font-mono-accent font-bold text-[#0A2342]">
                                R{product.price?.toLocaleString()}
                              </span>
                            )}
                          </div>
                          
                          <Button
                            size="sm"
                            onClick={() => handleAddToCart(product)}
                            disabled={addedItems[product.id]}
                            className={
                              addedItems[product.id]
                                ? "bg-green-600 text-white cursor-not-allowed"
                                : product.price_on_request 
                                  ? "bg-[#0A2342] text-white hover:bg-[#0A2342]/90" 
                                  : "bg-[#FF6B35] text-white hover:bg-[#FF6B35]/90"
                            }
                            data-testid={`add-to-cart-${product.id}`}
                          >
                            {addedItems[product.id] ? (
                              <>
                                <Check className="w-4 h-4 mr-1" />
                                Added!
                              </>
                            ) : product.price_on_request ? (
                              'Contact Us'
                            ) : (
                              <>
                                <ShoppingCart className="w-4 h-4 mr-1" />
                                Add
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </section>
      ))}

      {/* Fallback Products Section - Only shown if no products section exists in sections */}
      {products.length > 0 && !sections.some(s => s.section_type === 'products') && (
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <h2 className="text-3xl font-heading font-bold text-[#0A2342] mb-8 text-center">
              {brand.name} Products
            </h2>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {products.map((product) => (
                <div 
                  key={product.id}
                  className="bg-[#F0EFEA] rounded-lg overflow-hidden hover:shadow-xl transition-all duration-300 group"
                  data-testid={`brand-product-${product.id}`}
                >
                  <Link to={`/brand-product/${product.id}`}>
                    <div className="relative h-56 overflow-hidden flex items-center justify-center bg-white">
                      <img 
                        src={product.images?.[0] || 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=400'}
                        alt={product.name}
                        className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                      />
                      {product.featured && (
                        <Badge className="absolute top-3 left-3 bg-[#FF6B35] text-white">
                          Featured
                        </Badge>
                      )}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                        <Eye className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </div>
                  </Link>
                  
                  <div className="p-4">
                    <Link to={`/brand-product/${product.id}`}>
                      <h3 className="text-lg font-semibold text-[#0A2342] mb-2 hover:text-[#FF6B35] transition-colors">
                        {product.name}
                      </h3>
                    </Link>
                    
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                      {product.description}
                    </p>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        {product.price_on_request ? (
                          <span className="text-[#FF6B35] font-semibold">Price on Request</span>
                        ) : (
                          <span className="text-xl font-mono-accent font-bold text-[#0A2342]">
                            R{product.price?.toLocaleString()}
                          </span>
                        )}
                      </div>
                      
                      <Button
                        size="sm"
                        onClick={() => handleAddToCart(product)}
                        disabled={addedItems[product.id]}
                        className={
                          addedItems[product.id]
                            ? "bg-green-600 text-white cursor-not-allowed"
                            : product.price_on_request 
                              ? "bg-[#0A2342] text-white hover:bg-[#0A2342]/90" 
                              : "bg-[#FF6B35] text-white hover:bg-[#FF6B35]/90"
                        }
                        data-testid={`add-to-cart-${product.id}`}
                      >
                        {addedItems[product.id] ? (
                          <>
                            <Check className="w-4 h-4 mr-1" />
                            Added!
                          </>
                        ) : product.price_on_request ? (
                          'Contact Us'
                        ) : (
                          <>
                            <ShoppingCart className="w-4 h-4 mr-1" />
                            Add
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* No Products Message */}
      {products.length === 0 && (
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <p className="text-gray-500">Products coming soon. Check back later!</p>
          </div>
        </section>
      )}
    </div>
  );
};

export default BrandPage;
